import { Close } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNotification } from '../../contexts/NotificationContext';
import apiClient from '../../services/api';
import { logger } from '../../utils/logger';

interface AtlCtlModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

interface FitnessDataPoint {
  date: string;
  atl: number;
  ctl: number;
  tsb: number;
}

const AtlCtlModal: React.FC<AtlCtlModalProps> = ({ isOpen, onClose, userId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showApiError } = useNotification();
  const [fitnessData, setFitnessData] = useState<FitnessDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchFitnessHistory();
    }
  }, [isOpen, userId]);

  const fetchFitnessHistory = async () => {
    setIsLoading(true);
    try {
      logger.info('Fetching fitness history from intervals.icu...');
      const response = await apiClient.getFitnessHistory(90);

      if (response.success && response.data) {
        logger.info(`Received ${response.data.history.length} data points`);
        setFitnessData(response.data.history);
      } else {
        showApiError(response, 'Failed to fetch fitness history');
      }
    } catch (error) {
      logger.error('Error fetching fitness history', error);
      showApiError(
        error instanceof Error ? error.message : 'Unknown error',
        'Failed to fetch fitness history'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Format date for display (MMM DD)
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Prepare data for chart
  // On mobile, show only last 3 weeks (21 days)
  const displayData = isMobile ? fitnessData.slice(-21) : fitnessData;

  const chartData = displayData.map(point => ({
    date: formatDate(point.date),
    ATL: Math.round(point.atl),
    CTL: Math.round(point.ctl),
    TSB: Math.round(point.tsb),
  }));

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullScreen
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
      PaperProps={{
        sx: {
          m: { xs: 0, sm: 2, md: 3, lg: 4 },
          maxHeight: {
            xs: '100%',
            sm: 'calc(100% - 32px)',
            md: 'calc(100% - 48px)',
            lg: 'calc(100% - 64px)',
          },
          maxWidth: {
            xs: '100%',
            sm: 'calc(100% - 32px)',
            md: 'calc(100% - 48px)',
            lg: 'calc(100% - 64px)',
          },
          borderRadius: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Fitness Trend - Last {isMobile ? '3 Weeks' : '3 Months'}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close fitness trend chart">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pb: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
            <CircularProgress />
          </Box>
        ) : fitnessData.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
            <Typography variant="body1" color="text.secondary">
              No fitness data available. Make sure you're connected to Intervals.icu.
            </Typography>
          </Box>
        ) : (
          <>
            <Box flex={1} minHeight={0}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis
                    dataKey="date"
                    stroke={theme.palette.text.secondary}
                    tick={{ fill: theme.palette.text.secondary }}
                    tickMargin={10}
                    interval="preserveStartEnd"
                    minTickGap={30}
                  />
                  <YAxis
                    stroke={theme.palette.text.secondary}
                    tick={{ fill: theme.palette.text.secondary }}
                    label={{
                      value: 'Training Load',
                      angle: -90,
                      position: 'insideLeft',
                      style: { fill: theme.palette.text.secondary },
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                    }}
                    labelStyle={{ color: theme.palette.text.primary }}
                  />
                  <Legend />
                  <Bar
                    dataKey="TSB"
                    fill={theme.palette.success.main}
                    fillOpacity={0.6}
                    name={isMobile ? 'TSB' : 'TSB (Form)'}
                  />
                  <Line
                    type="monotone"
                    dataKey="ATL"
                    stroke={theme.palette.error.main}
                    strokeWidth={2}
                    dot={false}
                    name={isMobile ? 'ATL' : 'ATL (Fatigue)'}
                  />
                  <Line
                    type="monotone"
                    dataKey="CTL"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={false}
                    name={isMobile ? 'CTL' : 'CTL (Fitness)'}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AtlCtlModal;
